// src/utils/NotificationUtils.js

/**
 * Utilitaires pour gérer les notifications dans l'application
 */
class NotificationUtils {
  /**
   * Vérifie si les notifications sont prises en charge par le navigateur
   * @returns {boolean} Les notifications sont prises en charge
   */
  static isSupported() {
    return 'Notification' in window;
  }

  /**
   * Vérifie si le Service Worker est pris en charge
   * @returns {boolean} Le Service Worker est pris en charge
   */
  static isServiceWorkerSupported() {
    return 'serviceWorker' in navigator;
  }

  /**
   * Vérifie si les notifications Push sont prises en charge
   * @returns {boolean} Les notifications Push sont prises en charge
   */
  static isPushSupported() {
    return this.isServiceWorkerSupported() && 'PushManager' in window;
  }

  /**
   * Obtient l'état actuel de l'autorisation de notification
   * @returns {string} L'état d'autorisation ('default', 'granted', 'denied')
   */
  static getPermissionStatus() {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Demande l'autorisation d'envoyer des notifications
   * @returns {Promise<string>} L'état d'autorisation
   */
  static async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Les notifications ne sont pas prises en charge par ce navigateur');
      return 'denied';
    }

    try {
      console.log('Demande d\'autorisation pour les notifications...');
      const permission = await Notification.requestPermission();
      console.log('Résultat de la demande d\'autorisation:', permission);
      return permission;
    } catch (error) {
      console.error('Erreur lors de la demande d\'autorisation:', error);
      return 'denied';
    }
  }

  /**
   * Enregistre le Service Worker si nécessaire
   * @returns {Promise<ServiceWorkerRegistration|null>} L'enregistrement du Service Worker
   */
  static async getServiceWorkerRegistration() {
    if (!this.isServiceWorkerSupported()) {
      console.warn('Le Service Worker n\'est pas pris en charge par ce navigateur');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      console.log('Service Worker prêt:', registration);
      return registration;
    } catch (error) {
      console.error('Erreur lors de l\'obtention du Service Worker:', error);
      return null;
    }
  }

  /**
   * Envoie une notification à l'utilisateur
   * @param {Object} options - Options de notification
   * @param {string} options.title - Titre de la notification
   * @param {string} options.body - Corps de la notification
   * @param {string} [options.icon] - URL de l'icône
   * @param {boolean} [options.requireInteraction=false] - La notification reste visible jusqu'à interaction
   * @param {Array} [options.actions] - Actions disponibles dans la notification
   * @param {Object} [options.data] - Données associées à la notification
   * @returns {Promise<boolean>} Succès de l'envoi
   */
  static async showNotification(options) {
    if (!this.isSupported()) {
      console.warn('Les notifications ne sont pas prises en charge par ce navigateur');
      return false;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Permission de notification non accordée');
      return false;
    }

    try {
      // Essayer d'utiliser le Service Worker si disponible (pour les actions et interactions avancées)
      if (this.isServiceWorkerSupported()) {
        const registration = await this.getServiceWorkerRegistration();
        
        if (registration) {
          console.log('Envoi de notification via Service Worker:', options);
          
          await registration.showNotification(options.title, {
            body: options.body,
            icon: options.icon || './logo.png',
            badge: options.badge || './logo.png',
            vibrate: options.vibrate || [100, 50, 100],
            requireInteraction: options.requireInteraction || false,
            data: options.data || {},
            actions: options.actions || []
          });
          
          console.log('Notification envoyée avec succès via Service Worker');
          return true;
        }
      }
      
      // Fallback à l'API Notification standard
      console.log('Envoi de notification via API standard:', options);
      
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || './logo.png',
        requireInteraction: options.requireInteraction || false,
        data: options.data || {}
      });
      
      notification.onclick = () => {
        console.log('Notification cliquée');
        
        if (options.data && options.data.url) {
          window.open(options.data.url, '_blank');
        } else {
          window.focus();
        }
        
        notification.close();
      };
      
      console.log('Notification envoyée avec succès via API standard');
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
      return false;
    }
  }
  
  /**
   * Envoie une notification via le Service Worker
   * Utile pour les cas où l'on souhaite utiliser les fonctionnalités avancées du Service Worker
   * @param {Object} options - Options de notification
   * @returns {Promise<boolean>} Succès de l'envoi
   */
  static async sendViaServiceWorker(options) {
    if (!this.isServiceWorkerSupported()) {
      console.warn('Le Service Worker n\'est pas pris en charge par ce navigateur');
      return this.showNotification(options); // Fallback à la méthode standard
    }
    
    try {
      const registration = await this.getServiceWorkerRegistration();
      
      if (!registration) {
        console.warn('Service Worker non enregistré');
        return this.showNotification(options); // Fallback à la méthode standard
      }
      
      // Envoyer un message au Service Worker pour afficher la notification
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        notification: options
      });
      
      console.log('Message envoyé au Service Worker pour afficher la notification');
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'envoi via Service Worker:', error);
      return this.showNotification(options); // Fallback à la méthode standard
    }
  }
  
  /**
   * S'abonne aux notifications pour une commande spécifique
   * @param {string|number} orderId - ID de la commande
   * @returns {Promise<boolean>} Succès de l'abonnement
   */
  static async subscribeToOrderNotifications(orderId) {
    if (!this.isSupported()) {
      console.warn('Les notifications ne sont pas prises en charge par ce navigateur');
      return false;
    }
    
    // S'assurer que l'autorisation est accordée
    if (Notification.permission !== 'granted') {
      const permission = await this.requestPermission();
      
      if (permission !== 'granted') {
        console.warn('Permission de notification refusée');
        return false;
      }
    }
    
    try {
      // Stocker l'abonnement pour la commande dans IndexedDB
      await this.storeOrderSubscription(orderId);
      
      // Envoyer une notification de confirmation
      await this.showNotification({
        title: 'Notifications activées',
        body: 'Vous recevrez désormais des notifications sur le statut de votre commande.',
        data: {
          type: 'subscription_confirmation',
          orderId: orderId
        }
      });
      
      // Déclencher une vérification immédiate du statut
      if (this.isServiceWorkerSupported()) {
        const registration = await this.getServiceWorkerRegistration();
        
        if (registration && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CHECK_ORDER_STATUS',
            orderId: orderId
          });
        }
      }
      
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'abonnement aux notifications:', error);
      return false;
    }
  }
  
  /**
   * Stocker l'abonnement à une commande dans IndexedDB et localStorage
   * @param {string|number} orderId - ID de la commande
   * @returns {Promise<boolean>} Succès du stockage
   */
  static async storeOrderSubscription(orderId) {
    try {
      // Format de l'ID en chaîne
      const orderIdStr = orderId.toString();
      
      // Stocker dans localStorage (accessible de partout)
      try {
        const existingData = localStorage.getItem('orderSubscriptions');
        let subscriptions = [];
        
        if (existingData) {
          subscriptions = JSON.parse(existingData);
        }
        
        // Vérifier si l'abonnement existe déjà
        if (!subscriptions.some(sub => sub.id === orderIdStr)) {
          subscriptions.push({
            id: orderIdStr,
            timestamp: Date.now()
          });
          
          localStorage.setItem('orderSubscriptions', JSON.stringify(subscriptions));
        }
      } catch (e) {
        console.warn('Erreur lors du stockage dans localStorage:', e);
      }
      
      // Stocker dans IndexedDB (plus fiable et accessible au Service Worker)
      return new Promise((resolve, reject) => {
        try {
          const openRequest = indexedDB.open('DrinkDeliveryApp', 3);
          
          openRequest.onupgradeneeded = (event) => {
            const db = event.target.result;
            
            // Créer les object stores si nécessaire
            if (!db.objectStoreNames.contains('orderSubscriptions')) {
              db.createObjectStore('orderSubscriptions', { keyPath: 'id' });
            }
          };
          
          openRequest.onsuccess = (event) => {
            try {
              const db = event.target.result;
              const transaction = db.transaction(['orderSubscriptions'], 'readwrite');
              const store = transaction.objectStore('orderSubscriptions');
              
              const subscription = {
                id: orderIdStr,
                timestamp: Date.now()
              };
              
              const request = store.put(subscription);
              
              request.onsuccess = () => {
                console.log(`Abonnement stocké pour la commande ${orderIdStr}`);
                resolve(true);
              };
              
              request.onerror = (error) => {
                console.error(`Erreur lors du stockage de l'abonnement:`, error);
                resolve(false);
              };
            } catch (txError) {
              console.error('Erreur lors de la transaction:', txError);
              resolve(false);
            }
          };
          
          openRequest.onerror = (error) => {
            console.error(`Erreur lors de l'ouverture de la base de données:`, error);
            resolve(false);
          };
        } catch (error) {
          console.error('Erreur globale lors du stockage:', error);
          resolve(false);
        }
      });
    } catch (error) {
      console.error('Erreur lors du stockage de l\'abonnement:', error);
      return false;
    }
  }
  
  /**
   * Vérifie si l'utilisateur est abonné aux notifications d'une commande
   * @param {string|number} orderId - ID de la commande
   * @returns {Promise<boolean>} L'utilisateur est abonné
   */
  static async isSubscribedToOrder(orderId) {
    // D'abord vérifier dans localStorage (plus rapide)
    const orderIdStr = orderId.toString();
    
    try {
      const subscriptionsData = localStorage.getItem('orderSubscriptions');
      
      if (subscriptionsData) {
        const subscriptions = JSON.parse(subscriptionsData);
        
        if (Array.isArray(subscriptions) && subscriptions.some(sub => sub.id === orderIdStr)) {
          return true;
        }
      }
    } catch (e) {
      console.warn('Erreur lors de la vérification dans localStorage:', e);
    }
    
    // Ensuite vérifier dans IndexedDB
    return new Promise((resolve) => {
      try {
        const openRequest = indexedDB.open('DrinkDeliveryApp', 3);
        
        openRequest.onsuccess = (event) => {
          try {
            const db = event.target.result;
            
            if (!db.objectStoreNames.contains('orderSubscriptions')) {
              resolve(false);
              return;
            }
            
            const transaction = db.transaction(['orderSubscriptions'], 'readonly');
            const store = transaction.objectStore('orderSubscriptions');
            const request = store.get(orderIdStr);
            
            request.onsuccess = () => {
              resolve(!!request.result);
            };
            
            request.onerror = () => {
              resolve(false);
            };
          } catch (error) {
            console.error('Erreur lors de la transaction:', error);
            resolve(false);
          }
        };
        
        openRequest.onerror = () => {
          resolve(false);
        };
      } catch (error) {
        console.error('Erreur globale:', error);
        resolve(false);
      }
    });
  }
}

export default NotificationUtils;